import * as React from "react";
import { useApiService, useTl, useDeleteApi } from "../../../../hooks";
import { IDataTableColumn, useGridState, useSearchApi, FieldSet, ButtonContainer } from "nsitools-react";
import { ETLCodes } from "../../../../locales";
import { useParams } from "react-router";
import { AddButton, DeleteButton, CustomDataTable } from "../../../../components";
import { Intent, Dialog, Classes, Colors } from "@blueprintjs/core";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { VeterinaryClinicDto, VeterinaryClinicSearch, VeterinaryClinicApi } from "../../../../api";
import { ClinicVeterinariesPanelStack } from "./ClinicVeterinariesPanelStack";
import { Link } from "react-router-dom";

export interface IClinicVeterinariesPageProps {
  editMode: boolean;
}
export const ClinicVeterinariesPage: React.FunctionComponent<IClinicVeterinariesPageProps> = ({ editMode }) => {
  const api = useApiService(VeterinaryClinicApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const { id } = useParams<{ id: string }>();
  const [dialogOpened, setDialogOpened] = React.useState(false);

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { name: "ASC" },
  });

  const searchFunction = React.useCallback((sObj?: VeterinaryClinicSearch) => {
    return api.baseSearch(
      VeterinaryClinicSearch.fromJS({ ...sObj, clinicId: +id, languageId: currentLanguage.languageId })
    );
  }, []);

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const { deleteItem } = useDeleteApi(
    React.useMemo(
      () => ({
        deleteApiFn: (data: number) => api.deleteVeterinaryClinic(data),
        onDeletedFn: () => search(),
      }),
      [api, search]
    )
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        hidden: !editMode,
        autoFitContent: true,
        render: (row: VeterinaryClinicDto) => (
          <ButtonContainer>
            <DeleteButton minimal={true} onDelete={() => deleteItem(row.clinicVeterinaryId)} />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Name),
        fieldName: "name",
        render: (row: VeterinaryClinicDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.veterinaryPersonId}/detail/view`}>
            {row.name}
          </Link>
        ),
      },
    ],
    [deleteItem, editMode, t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);
  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);
  return (
    <FieldSet
      title={t(ETLCodes.Veterinarians)}
      rightElement={
        editMode && (
          <AddButton
            onClick={(e) => {
              e.stopPropagation();
              addItemFunction();
            }}
            text={t(ETLCodes.General_Add)}
            intent={Intent.PRIMARY}
          />
        )
      }
    >
      <CustomDataTable tableState={tableState} loading={loading} columns={columns} />

      <Dialog
        title={t(ETLCodes.Veterinarians)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1000, height: 750, background: Colors.WHITE }}
        className={Classes.DIALOG}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <ClinicVeterinariesPanelStack
            clinicId={+id}
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
          />
        </div>
      </Dialog>
    </FieldSet>
  );
};
